import { StainType } from 'interfaces/stainType';
import { Taxonomy } from 'interfaces/taxonomy';
import { InputSource } from 'interfaces/postProcessingAction';
import { compact, filter, find, includes, last, map, sortBy, split, startsWith, toString, uniq } from 'lodash';

export type BulkSelectionValue = string | number;

export interface BulkSelectionOption {
  id: BulkSelectionValue;
  label: string;
  disabled?: boolean;
  unavailable?: boolean;
  deleted?: boolean;
  qcFailed?: boolean;
  ignored?: boolean;
}

export type BulkSelectionExclusionReason = 'unavailable' | 'deleted_metadata' | 'qc_failed' | 'ignored';

export interface BulkSelectionExclusion {
  id: BulkSelectionValue;
  label: string;
  reason: BulkSelectionExclusionReason;
}

export interface BulkSelectionResult {
  selectedIds: BulkSelectionValue[];
  excluded: BulkSelectionExclusion[];
}

export interface BulkSelectionShortcut {
  id: string;
  label: string;
  result: BulkSelectionResult;
}

export type MarkerMetadataGroup = 'membrane' | 'nuclear' | 'cytoplasmic' | 'dna' | 'phenotypic';

const markerMetadataKeyByGroup: Record<MarkerMetadataGroup, keyof StainType> = {
  membrane: 'membrane',
  nuclear: 'nucleus',
  cytoplasmic: 'cytoplasm',
  dna: 'dna',
  phenotypic: 'phenotypic',
};

const getOptionExclusionReason = (option: BulkSelectionOption): BulkSelectionExclusionReason | null => {
  if (option.disabled || option.unavailable) {
    return 'unavailable';
  }
  if (option.qcFailed) {
    return 'qc_failed';
  }
  if (option.ignored) {
    return 'ignored';
  }
  if (option.deleted) {
    return 'deleted_metadata';
  }
  return null;
};

const buildSelectionResult = (
  options: BulkSelectionOption[],
  getAdditionalExclusionReason?: (option: BulkSelectionOption) => BulkSelectionExclusionReason | null
): BulkSelectionResult => {
  const excluded = compact(
    map(options, (option) => {
      const reason = getOptionExclusionReason(option) || getAdditionalExclusionReason?.(option);
      return reason ? { id: option.id, label: option.label, reason } : null;
    })
  );
  const excludedIds = map(excluded, 'id');

  return {
    selectedIds: map(
      filter(options, (option) => !includes(excludedIds, option.id)),
      'id'
    ),
    excluded,
  };
};

export const selectMarkerGroupOptions = (
  options: BulkSelectionOption[],
  stainTypes: StainType[],
  group: MarkerMetadataGroup
): BulkSelectionResult => {
  const metadataKey = markerMetadataKeyByGroup[group];
  const matchingOptions = filter(options, (option) => {
    const stainType = find(stainTypes, (candidate) => candidate.id === toString(option.id));
    return Boolean(stainType?.[metadataKey]);
  });

  return buildSelectionResult(matchingOptions, (option) => {
    const stainType = find(stainTypes, (candidate) => candidate.id === toString(option.id));
    return stainType?.deletedAt ? 'deleted_metadata' : null;
  });
};

export const selectImmuneCellOptions = (
  options: BulkSelectionOption[],
  taxonomies: Taxonomy[],
  immuneRootPath = 'tissue.cell.benign_cell.immune_cell'
): BulkSelectionResult => {
  const immuneTaxonomies = filter(
    taxonomies,
    (taxonomy) =>
      !taxonomy.isAbstract && (taxonomy.path === immuneRootPath || startsWith(taxonomy.path, `${immuneRootPath}.`))
  );
  const immuneIds = uniq(
    compact(map(immuneTaxonomies, (taxonomy) => [taxonomy.path, last(split(taxonomy.path, '.'))])).flat()
  );
  const matchingOptions = filter(options, (option) => includes(immuneIds, toString(option.id)));

  return buildSelectionResult(matchingOptions);
};

export const selectAllAvailableOptions = (options: BulkSelectionOption[]): BulkSelectionResult =>
  buildSelectionResult(options);

export const addBulkSelectionValues = (
  currentValues: BulkSelectionValue[],
  bulkSelectedValues: BulkSelectionValue[],
  limit?: number
): BulkSelectionValue[] => {
  const combinedValues = sortBy(uniq([...currentValues, ...bulkSelectedValues]), toString);
  return limit ? combinedValues.slice(0, limit) : combinedValues;
};

const markerShortcutLabels: Record<MarkerMetadataGroup, string> = {
  membrane: 'All membrane markers',
  nuclear: 'All nuclear markers',
  cytoplasmic: 'All cytoplasmic markers',
  dna: 'All DNA markers',
  phenotypic: 'All phenotypic markers',
};

const areaInputSources = [
  InputSource.AREAS,
  InputSource.INTERMEDIATE_AREAS_PER_TARGET_STAIN,
  InputSource.CLASSES_CONFIG_AREAS_PER_TARGET_STAIN,
];

const resultAttributeInputSources = [
  InputSource.CELL_ATTRIBUTES_OF_SELECTED_SLIDES,
  InputSource.CELL_ATTRIBUTES_OF_SELECTED_SLIDES_FOR_REASSIGNMENT,
  InputSource.AREA_ATTRIBUTES_OF_SELECTED_SLIDES,
  InputSource.AREA_ATTRIBUTES_OF_SELECTED_SLIDES_FOR_REASSIGNMENT,
];

export const getBulkSelectionShortcuts = ({
  inputSource,
  options,
  stainTypes,
  taxonomies,
}: {
  inputSource?: string | string[];
  options: BulkSelectionOption[];
  stainTypes: StainType[];
  taxonomies: Taxonomy[];
}): BulkSelectionShortcut[] => {
  if (Array.isArray(inputSource)) {
    return [];
  }

  const markerShortcuts = compact(
    map(markerShortcutLabels, (label, group: MarkerMetadataGroup) => {
      const result = selectMarkerGroupOptions(options, stainTypes, group);
      return result.selectedIds.length || result.excluded.length ? { id: `markers:${group}`, label, result } : null;
    })
  );
  const immuneResult = selectImmuneCellOptions(options, taxonomies);
  const immuneShortcut =
    immuneResult.selectedIds.length || immuneResult.excluded.length
      ? [{ id: 'cells:immune', label: 'All immune cell types', result: immuneResult }]
      : [];

  if (includes(areaInputSources, inputSource)) {
    return [{ id: 'areas:available', label: 'All available areas', result: selectAllAvailableOptions(options) }];
  }

  if (includes(resultAttributeInputSources, inputSource)) {
    return [
      {
        id: 'attributes:available',
        label: 'All available result attributes',
        result: selectAllAvailableOptions(options),
      },
    ];
  }

  return [...markerShortcuts, ...immuneShortcut];
};
